import { useRef, useEffect } from "react";
import { AddressComponents } from "./AddressComponents"; // Adjust the import path as necessary

interface GoogleSearchBoxProps {
  onPlaceSelected: (address: AddressComponents) => void;
  initialValue?: string;
  onInputChange?: (value: string) => void;
}

export default function GoogleSearchBox({
  onPlaceSelected,
  initialValue = "",
  onInputChange,
}: GoogleSearchBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Set initial value when component mounts or initialValue changes
  useEffect(() => {
    if (inputRef.current && initialValue) {
      inputRef.current.value = initialValue;
    }
  }, [initialValue]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onInputChange) {
      onInputChange(e.target.value);
    }
  };

  useEffect(() => {
    // Check if Google Maps is already loaded (by the GoogleMap component)
    const initializeAutocomplete = () => {
      if (
        inputRef.current &&
        !autocompleteRef.current &&
        window.google &&
        window.google.maps &&
        window.google.maps.places
      ) {
        // Set initial value if provided
        if (initialValue) {
          inputRef.current.value = initialValue;
        }

        autocompleteRef.current = new google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["geocode"], // restrict to geographic locations
            fields: [
              "address_components",
              "formatted_address",
              "geometry.location",
            ],
          }
        );

        // Add event listener for when a place is selected
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current?.getPlace();

          if (place && place.address_components && place.geometry?.location) {
            const address: AddressComponents = {
              fullAddress: place.formatted_address,
              location: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              },
            };

            // Parse address components
            place.address_components.forEach((component) => {
              if (
                component.types.includes("street_number") ||
                component.types.includes("route")
              ) {
                address.street = address.street
                  ? `${address.street} ${component.long_name}`
                  : component.long_name;
              }
              if (component.types.includes("locality")) {
                address.city = component.long_name;
              }
              if (component.types.includes("administrative_area_level_1")) {
                address.state = component.long_name;
              }
              if (component.types.includes("country")) {
                address.country = component.long_name;
              }
              if (component.types.includes("postal_code")) {
                address.postalCode = component.long_name;
              }
            });

            console.log("Full address details:", address);
            onPlaceSelected(address);
          }
        });
      }
    };

    // Try to initialize immediately if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeAutocomplete();
    } else {
      // Wait for Google Maps to load and try again
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGoogleMaps);
          initializeAutocomplete();
        }
      }, 100);

      return () => {
        clearInterval(checkGoogleMaps);
        // Clean up autocomplete instance when component unmounts
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      };
    }

    return () => {
      // Clean up autocomplete instance when component unmounts
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onPlaceSelected]);

  return (
    <div className="flex flex-col w-100 gap-1 px-4 p-4 border-gray-500 rounded-2xl hover:bg-gray-100 transition-colors cursor-pointer">
      <input
        ref={inputRef}
        type="text"
        placeholder="Search suburb, postcode or state"
        defaultValue={initialValue}
        className="w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm"
        id="location-search"
        onChange={handleInputChange}
      />
    </div>
  );
}
